/**
 * Radial dendritic layout.
 *
 * Classic radial-tree angular partitioning (each subtree owns a wedge sized by
 * its leaf count, so strands can never cross) with two departures that matter:
 *
 *  - Ring spacing *decays* with depth. Reasoning fans out fastest near the
 *    premise and refines as it goes; equal rings would make depth-6 detail
 *    look as consequential as a first-order branch.
 *  - Every position carries a seeded jitter. Perfect rings read as a diagram;
 *    a few pixels of irregularity read as growth.
 *
 * Deterministic: same graph in, same coordinates out, every time.
 */

import type { ReasonNode } from './types'
import { TAU, hash01 } from './geometry'

const RING_BASE = 300
const RING_DECAY = 0.875
const RING_MIN = 142
/**
 * Extra field between the seed and its first ring, world units. The seed is a
 * bare title (D350), and the first ring used to start where the old orb's glow
 * ended — close enough that first-ring nodes and their strands crowded the
 * words. Added once, to the first ring, so every ring moves out together and
 * the gaps between rings are unchanged.
 */
export const SEED_GAP = 360
/** Leaves of a wide subtree crowd; give each subtree a hair of breathing room. */
const WEDGE_INSET = 0.92

/**
 * How far a node may be pulled along its own spoke, in world units.
 *
 * The gesture exists because the declutter pass drops a caption that has
 * nowhere to sit, and the reader's answer to that should not have to be "zoom
 * in and lose the shape". Pulling a node outward buys its label room without
 * touching the node's *angle* — so the wedge partitioning still holds and
 * strands still cannot cross, which is the one property of this layout worth
 * protecting from a mouse.
 *
 * Outward is generous (a little over a full base ring) because that is the
 * direction that actually buys space. Inward is deliberately not: the tightest
 * ring gap is RING_MIN less the ±17 of jitter, so −80 is the most that can be
 * taken back before a node starts arriving on top of its own parent.
 */
export const STRETCH_MIN = -80
export const STRETCH_MAX = 320
/**
 * Below this, a stretch is no stretch.
 *
 * A drag has to be able to put a node back, and hitting an exact zero by hand
 * is not a thing anyone can do — so the last few pixels snap home. It also
 * keeps a stored 2-unit offset, which is invisible and pins the camera, from
 * outliving the twitch that made it.
 */
const STRETCH_SNAP = 14

/** The only way a stretch should ever be written. Snaps home, then bounds. */
export function clampStretch(v: number): number {
  if (!Number.isFinite(v) || Math.abs(v) < STRETCH_SNAP) return 0
  return Math.min(STRETCH_MAX, Math.max(STRETCH_MIN, v))
}

/**
 * The gesture's running total, bounded but NOT snapped.
 *
 * A drag accumulates pointer deltas, and a pointer delivers them a few pixels
 * at a time. Feeding that total back through `clampStretch` — which is what a
 * drag did at first — snaps every sub-14 increment to zero, so the total never
 * grows: a slow drag moves nothing however far it travels, and a fast one works
 * only because a single event happened to clear the threshold in one go.
 * Crossing back through zero deadlocks the same way.
 *
 * So the accumulator keeps its raw value and only what is *read* off it — the
 * layout during the drag, and the value committed at the end — goes through
 * `clampStretch`. The bound is still applied here, because an accumulator that
 * ran to 5,000 while the node sat at its limit would have to be dragged all the
 * way back before the node moved at all.
 */
export function stretchRange(v: number): number {
  if (!Number.isFinite(v)) return 0
  return Math.min(STRETCH_MAX, Math.max(STRETCH_MIN, v))
}

/**
 * Everything `layout()` will move when this node's stretch changes — the node,
 * its descendants, and any synthesis seated off it, with their descendants too.
 *
 * The drag snaps this set to its new coordinates while the rest of the graph
 * eases, so anything missing from it lags the pointer by the ease constant. A
 * `parentId` walk alone is not the answer, because a synthesis is seated off
 * BOTH its `bridgeFrom` parents and in general neither of them is its
 * `parentId`: drag the other one and the bridge moves in `layout()` while the
 * drag leaves it easing, so the parent renders outside the synthesis it feeds
 * for as long as the ease takes — the inward-running strand the layout is
 * careful never to produce, restored for the duration of the gesture.
 *
 * Here rather than inline in the canvas so the reverse edge is testable; the
 * canvas has no test of its own to catch this.
 */
export function stretchKin(nodes: Record<string, ReasonNode>, order: string[], id: string) {
  const kids = new Map<string, string[]>()
  const bridged = new Map<string, string[]>()
  const add = (m: Map<string, string[]>, k: string, v: string) => {
    const l = m.get(k)
    if (l) l.push(v)
    else m.set(k, [v])
  }
  for (const nid of order) {
    const n = nodes[nid]
    if (!n) continue
    if (n.parentId) add(kids, n.parentId, nid)
    for (const p of n.bridgeFrom ?? []) add(bridged, p, nid)
  }

  const seen = new Set<string>([id])
  const out = [id]
  // Iterative and visited-guarded: a bridge can point back up its own branch,
  // so this graph is not a tree and a recursive walk would not terminate.
  for (let i = 0; i < out.length; i++) {
    for (const c of kids.get(out[i]!) ?? []) {
      if (seen.has(c)) continue
      seen.add(c)
      out.push(c)
    }
    for (const c of bridged.get(out[i]!) ?? []) {
      if (seen.has(c)) continue
      seen.add(c)
      out.push(c)
    }
  }
  return out
}

export interface Bounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/**
 * Half-extent of the caption a node carries. Bounds computed from bare node
 * coordinates frame the *dots* and clip every rim label; every node here owns
 * a text box, so the box is what has to fit.
 */
const LABEL_HALF_W = 112
const LABEL_HALF_H = 36

function grow(b: Bounds, x: number, y: number) {
  if (x - LABEL_HALF_W < b.minX) b.minX = x - LABEL_HALF_W
  if (y - LABEL_HALF_H < b.minY) b.minY = y - LABEL_HALF_H
  if (x + LABEL_HALF_W > b.maxX) b.maxX = x + LABEL_HALF_W
  if (y + LABEL_HALF_H > b.maxY) b.maxY = y + LABEL_HALF_H
}

export function ringRadius(depth: number): number {
  let r = depth > 0 ? SEED_GAP : 0
  for (let i = 0; i < depth; i++) r += Math.max(RING_MIN, RING_BASE * Math.pow(RING_DECAY, i))
  return r
}

/** Leaf count of each subtree — the weight each wedge is sized by. */
function weigh(
  id: string,
  nodes: Record<string, ReasonNode>,
  kids: Map<string, string[]>,
  memo: Map<string, number>,
  guard: Set<string>,
): number {
  const hit = memo.get(id)
  if (hit !== undefined) return hit
  if (guard.has(id)) return 1
  // A folded subtree is one mark, so it is worth one leaf of angle. Past about
  // eighty nodes the circle is the scarce resource, and a branch you have
  // settled should stop spending it.
  if (nodes[id]?.collapsed) {
    memo.set(id, 1)
    return 1
  }
  guard.add(id)
  const cs = kids.get(id)
  let w: number
  if (!cs || cs.length === 0) {
    w = 1
  } else {
    w = 0
    for (const c of cs) w += weigh(c, nodes, kids, memo, guard)
  }
  guard.delete(id)
  memo.set(id, w)
  return w
}

/**
 * Assign `x`/`y`/`angle`/`radius`/`depth` in place.
 * Returns the world bounds, so the camera can frame the whole graph.
 *
 * `live` is one node's stretch as it is being dragged, standing in for whatever
 * is stored on it. It exists so the drag can re-lay-out straight onto the node
 * objects the render loop reads, without a store commit — and therefore without
 * a React render and a camera refit — on every pointer move. See NeuralCanvas.
 */
export function layout(
  nodes: Record<string, ReasonNode>,
  order: string[],
  live?: { id: string; stretch: number } | null,
): Bounds {
  const kids = new Map<string, string[]>()
  let rootId: string | null = null

  for (const id of order) {
    const n = nodes[id]
    if (!n) continue
    if (n.parentId === null) {
      if (rootId === null) rootId = id
      continue
    }
    if (!nodes[n.parentId]) continue // orphan; skipped rather than crashing
    const list = kids.get(n.parentId)
    if (list) list.push(id)
    else kids.set(n.parentId, [id])
  }

  if (!rootId) return { minX: -1, minY: -1, maxX: 1, maxY: 1 }

  const memo = new Map<string, number>()
  const guard = new Set<string>()
  const bounds: Bounds = { minX: 0, minY: 0, maxX: 0, maxY: 0 }
  const seen = new Set<string>()

  const stretchOf = (id: string) =>
    clampStretch(live && live.id === id ? live.stretch : (nodes[id]?.stretch ?? 0))

  const root = nodes[rootId]!
  root.depth = 0
  root.angle = 0
  root.radius = 0
  root.x = 0
  root.y = 0
  // The orb is 108 world units; a lone seed still needs a frame around it.
  bounds.minX = -150
  bounds.minY = -150
  bounds.maxX = 150
  bounds.maxY = 150

  /**
   * Iterative walk (an adversarial graph could be deep enough to blow the
   * stack, and a visualiser should never crash on its own data).
   */
  /**
   * `push` is the accumulated stretch of every ancestor on this path.
   *
   * A stretch travels down the branch rather than moving one node alone. Moving
   * the node by itself would let it walk past its own children — the tightest
   * ring gap is smaller than the outward range — and a strand that runs inward
   * says the reasoning derives backwards. Carried down, the gap the drag opens
   * is exactly the one above the node, which is the gap the caption needed, and
   * every gap below it is untouched.
   */
  const stack: Array<{ id: string; a0: number; a1: number; depth: number; push: number }> = [
    { id: rootId, a0: -Math.PI / 2, a1: -Math.PI / 2 + TAU, depth: 0, push: 0 },
  ]

  while (stack.length) {
    const frame = stack.pop()!
    if (seen.has(frame.id)) continue
    seen.add(frame.id)

    const node = nodes[frame.id]
    if (!node) continue
    // Stop at a fold. Because `grow(bounds, …)` is only called inside this loop,
    // a folded subtree also drops out of the returned bounds — so the framing
    // tightens on a fold, for free.
    if (node.collapsed && node.parentId !== null) continue

    const children = kids.get(frame.id)
    if (!children || children.length === 0) continue

    const span = frame.a1 - frame.a0
    // Below the root, pull the wedge in so sibling subtrees don't graze.
    const inset = frame.depth === 0 ? 1 : WEDGE_INSET
    const usable = span * inset
    const start = frame.a0 + (span - usable) / 2

    let total = 0
    const weights = children.map((c) => {
      const w = weigh(c, nodes, kids, memo, guard)
      total += w
      return w
    })
    if (total <= 0) total = children.length

    let cursor = start
    for (let i = 0; i < children.length; i++) {
      const cid = children[i]!
      const child = nodes[cid]
      if (!child) continue

      const share = (weights[i]! / total) * usable
      const c0 = cursor
      const c1 = cursor + share
      cursor = c1

      const depth = frame.depth + 1
      const h = hash01(cid)
      // Jitter is a fraction of the node's own wedge, so a crowded region
      // stays orderly while a sparse one gets visible irregularity.
      const wobble = (h - 0.5) * Math.min(share * 0.34, 0.14)
      const angle = (c0 + c1) / 2 + wobble
      const push = frame.push + stretchOf(cid)
      const radius = ringRadius(depth) + (hash01(cid + '·r') - 0.5) * 34 + push

      child.depth = depth
      child.angle = angle
      child.radius = radius
      child.x = Math.cos(angle) * radius
      child.y = Math.sin(angle) * radius

      grow(bounds, child.x, child.y)

      stack.push({ id: cid, a0: c0, a1: c1, depth, push })
    }
  }

  // Every id behind a fold, mapped to the stub it will be parked on. Built
  // before the bridge pass so a folded synthesis node is never seated: seating
  // calls grow(), and the whole point of a fold is that the framing tightens.
  const parked = new Map<string, string>()
  for (const id of order) {
    if (!nodes[id]?.collapsed) continue
    const st = [...(kids.get(id) ?? [])]
    while (st.length) {
      const cid = st.pop()!
      if (parked.has(cid)) continue
      parked.set(cid, id)
      for (const g of kids.get(cid) ?? []) st.push(g)
    }
  }

  // Synthesis nodes answer to two parents. Seat them on the angular bisector,
  // outside both, so the bridge visibly spans the gap it closes.
  for (const id of order) {
    const n = nodes[id]
    if (!n?.bridgeFrom || n.bridgeFrom.length < 2) continue
    if (parked.has(id)) continue
    const a = nodes[n.bridgeFrom[0]!]
    const b = nodes[n.bridgeFrom[1]!]
    if (!a || !b) continue

    let d = b.angle - a.angle
    while (d > Math.PI) d -= TAU
    while (d < -Math.PI) d += TAU
    const angle = a.angle + d / 2
    // Both parents' radii already carry their own branch's push, so a bridge
    // inherits the further one and then answers for its own stretch.
    const radius = Math.max(a.radius, b.radius) + 186 + stretchOf(id)

    // How far this reseat moves the node from where the tree walk left it. The
    // walk placed this node's descendants against that earlier radius, and it
    // reached them down the `parentId` chain — which does not pass through the
    // SECOND parent at all. So a stretch on that second parent lifts the
    // synthesis by way of `Math.max` above while its own children stay put, and
    // a large enough one lands the synthesis outside them: a strand running
    // back toward the premise, which is the one thing the stretch is not
    // allowed to produce. Carrying the displacement down the subtree is what
    // keeps that guarantee true for a bridge as well as for an ordinary node.
    const shift = radius - n.radius

    n.angle = angle
    n.radius = radius
    n.x = Math.cos(angle) * radius
    n.y = Math.sin(angle) * radius
    n.depth = Math.max(a.depth, b.depth) + 1

    grow(bounds, n.x, n.y)

    if (shift !== 0) {
      // Iterative and visited-guarded, like the main walk: this runs over graph
      // data, and a cycle here would hang the renderer rather than mis-draw it.
      const moved = new Set<string>([id])
      const st = [...(kids.get(id) ?? [])]
      while (st.length) {
        const cid = st.pop()!
        if (moved.has(cid)) continue
        moved.add(cid)
        const c = nodes[cid]
        if (!c) continue
        // A folded descendant is parked on its stub by the pass below, which
        // overwrites whatever is written here — so it is skipped, and with it
        // the `grow` that would have re-expanded the framing a fold just tightened.
        if (!parked.has(cid)) {
          c.radius += shift
          c.x = Math.cos(c.angle) * c.radius
          c.y = Math.sin(c.angle) * c.radius
          grow(bounds, c.x, c.y)
        }
        for (const g of kids.get(cid) ?? []) st.push(g)
      }
    }
  }

  // Folded descendants are parked on their stub. They are not drawn, so their
  // angle means nothing — but `motion` eases every node in `order` toward its
  // target, so this is what makes a fold retract inward and an unfold fan back
  // out, with no animation state anywhere.
  //
  // It writes x/y/angle/radius only. It must NOT write `depth`: depth is read
  // by the caption priority, the depth recession and the inspector, and
  // flattening it corrupts all three.
  for (const [cid, stubId] of parked) {
    const c = nodes[cid]
    const stub = nodes[stubId]
    if (!c || !stub) continue
    c.angle = stub.angle
    c.radius = stub.radius
    c.x = stub.x
    c.y = stub.y
  }

  // A small uniform margin on top of the per-node label allowance, so the
  // outermost caption is inside the frame rather than flush against it.
  return {
    minX: bounds.minX - 50,
    minY: bounds.minY - 40,
    maxX: bounds.maxX + 50,
    maxY: bounds.maxY + 40,
  }
}
